import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
/**
 * The one place the "no size chosen" message is worded. It is a full instruction now
 * rather than a label fragment, because the button itself no longer changes text --
 * it always reads "Add to cart", so the error has to carry the whole instruction.
 */
const CHOOSE_SIZE_MESSAGE = () =>
  // Deliberately does NOT fall back to `select_size_prompt`. That key holds the old
  // BUTTON LABEL ("Select your size"), which reads as a fragment here now that the
  // error carries the whole instruction on its own.
  Theme?.translations?.choose_size_error || 'Choose your size to add to cart.';

/**
 * Same instruction, worded for the AnywherePOD (APOD) Personalize button. That button
 * opens the personalizer modal rather than adding to cart, so "to add to cart" would
 * name an action the shopper did not take.
 */
const CHOOSE_SIZE_MESSAGE_PERSONALIZE = () =>
  Theme?.translations?.choose_size_error_personalize || 'Choose your size to personalize.';

/**
 * EVERY `button[name="add"]` in the add-to-cart container, not just the first one.
 *
 * Personalizer apps clone the Add to cart button. Customily's copy keeps `name="add"`,
 * sits BEFORE the theme's real button (`ref="addToCartButton"`) and hides it, so a
 * `querySelector` here silently returned the clone: `data-pj-needs-size` was set and
 * cleared on the copy while the real button kept whatever it rendered with. Since the
 * real button is the one Customily triggers after the design validates, the size guard
 * saw a permanently stale "awaiting a size" and blocked every add on every Customily
 * PDP. Write the state to all copies so no button can drift out of sync.
 *
 * On an ordinary PDP there is exactly one match and behaviour is unchanged.
 * @param {Element | null | undefined} container
 * @returns {HTMLElement[]}
 */
const addToCartButtonsIn = (container) =>
  container ? /** @type {HTMLElement[]} */ ([...container.querySelectorAll('button[name="add"]')]) : [];

/** Missing photo on an APOD inline-upload product. */
const UPLOAD_REQUIRED_MESSAGE = () =>
  Theme?.translations?.upload_required_error || 'Add a photo of your dog to continue.';

/** Any other required APOD personalization field left blank. */
const PERSONALIZATION_REQUIRED_MESSAGE = () =>
  Theme?.translations?.personalization_required_error || 'Fill in the personalization details to continue.';

class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // The size guard has to hang off CLICK, not submit. The theme's declarative
    // `on:click` binding calls preventDefault() on the button between capture and
    // bubble, so a click on this submit button never produces a submit event and
    // handleSubmit can never run. Verified in the browser: capture sees
    // defaultPrevented=false, bubble sees true, and no submit event is dispatched.
    this.addEventListener('click', this.#guardSizeSelection, { signal, capture: true });

    this.#updateAddToCartStateForSize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Blocks a click on the Add to cart button while no size is chosen, and guides the
   * shopper to the size row instead. The button is deliberately NOT disabled in this
   * state -- a disabled button is unfocusable and cannot be tapped at all -- so this
   * is what stops the add.
   * @param {MouseEvent} event
   */
  #guardSizeSelection = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target?.closest?.('button[name="add"], .js_apod_personalize_btn');
    if (!button) return;

    // APOD (AnywherePOD) CLONES the Add to cart button into `.js_apod_personalize_btn`
    // and hides the native one, so on those PDPs the clone is the only control the
    // shopper can reach and it must be guarded too. Two things make it a special case:
    //   1. The clone carries no `name="add"`, which is why the original selector missed
    //      it and the Personalize modal opened with nothing chosen.
    //   2. The clone copies `data-pj-needs-size` ONCE, at clone time, and APOD never
    //      updates it -- verified live: after a size is picked the native button drops
    //      the attribute and the clone still has it. Gating the clone on that attribute
    //      would therefore block Personalize forever. Read the live variant id instead;
    //      it is empty until a full variant is chosen and is the same structural signal
    //      the rest of this file relies on.
    // CUSTOMILY does the same clone trick, but worse: its copy KEEPS `name="add"`, is
    // inserted BEFORE the theme's real button and hides the real one. So the clone was
    // what `querySelector('button[name="add"]')` found further down this file, the real
    // button never had `data-pj-needs-size` cleared -- and the real button is exactly the
    // one Customily triggers once the design is valid. Every Customily PDP was therefore
    // blocked on "Choose your size to add to cart." no matter what the shopper picked.
    // That sync is fixed below; this stays as the backstop. The attribute alone is never
    // enough to BLOCK an add -- require the structural signal to agree, the same live
    // variant id the APOD branch reads. It is empty until a full variant is chosen, so a
    // stale attribute on any copy of the button can no longer strand a shopper.
    const isPersonalizeButton = button.classList.contains('js_apod_personalize_btn');
    const needsSize = isPersonalizeButton
      ? !this.refs.variantId?.value
      : button.hasAttribute('data-pj-needs-size') && !this.refs.variantId?.value;
    if (!needsSize) {
      // Size is settled. The APOD inline-upload products still need a native submit.
      if (!isPersonalizeButton) this.#submitNativelyForApodUpload(event);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    // APOD binds its own click listener that opens the modal. stopImmediatePropagation
    // is what keeps that listener from running; preventDefault alone does not.
    if (isPersonalizeButton) event.stopImmediatePropagation();

    const productRoot = this.closest('.shopify-section, dialog, product-card') || this;
    const sizeGroup = productRoot.querySelector('.variant-option[data-is-size-option="true"]');
    if (!sizeGroup) return;
    const sizeSelect = /** @type {HTMLSelectElement | null} */ (
      sizeGroup.querySelector('select.variant-option__select') || null
    );
    this.promptForSize(
      sizeGroup,
      sizeSelect,
      isPersonalizeButton ? CHOOSE_SIZE_MESSAGE_PERSONALIZE() : CHOOSE_SIZE_MESSAGE()
    );
  };

  /**
   * APOD (AnywherePOD) inline personalization -- previews OFF.
   *
   * In that mode APOD renders a plain `<input type="file">` on the page and does NOT
   * upload it anywhere; its change handler only writes the string 'Uploaded File' into a
   * hidden property. Delivering the actual bytes is the THEME's job, and this theme adds
   * to cart over AJAX (`/cart/add.js`), whose body is a URL-encoded string. A file cannot
   * ride in a string, so the browser drops it silently: the shopper picks a photo, sees
   * the filename, pays, and the order arrives with no image.
   *
   * Shopify's AJAX Cart API does not support file uploads at all -- file line item
   * properties require a native `multipart/form-data` POST, which Shopify then stores,
   * returning a cdn.shopify.com URL as the property value. Verified by hand on this store
   * using APOD's own field name.
   *
   * So when the form owns a visible APOD file input, submit NATIVELY instead. `form.submit()`
   * deliberately bypasses the theme's submit handler (and therefore the AJAX add). Cost: a
   * full page load to /cart rather than the drawer. Correct beats slick when the alternative
   * is losing the customer's photo.
   *
   * Scoped by capability, not by template: no APOD file input, no change in behaviour.
   *
   * @param {MouseEvent} event
   */
  #submitNativelyForApodUpload = (event) => {
    const form = /** @type {HTMLFormElement | null} */ (this.querySelector('form[action="/cart/add"]'));
    if (!form) return;

    // APOD's fields sit OUTSIDE the <form> and are associated by the `form=` attribute, so
    // form.querySelectorAll() misses them. `input.form` resolves the real form owner, and a
    // native submit collects them for the same reason.
    // `disabled` is belt and braces: APOD ships a hidden, disabled scaffold of template
    // fields on EVERY product (measured on the mug and blanket, which run previews ON and
    // must keep the AJAX add). Those are already invisible, but a disabled control never
    // submits, so it must never be what flips this product to a native POST.
    const owned = (selector) =>
      Array.from(document.querySelectorAll(selector)).filter(
        (el) =>
          /** @type {any} */ (el).form === form &&
          !(/** @type {HTMLInputElement} */ (el).disabled) &&
          /** @type {HTMLElement} */ (el).offsetParent !== null
      );

    const fileInputs = /** @type {HTMLInputElement[]} */ (owned('input[type="file"].apod-field'));
    if (!fileInputs.length) return;

    const stop = () => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    };

    // form.submit() skips the browser's constraint validation as well as the theme's
    // handler, so the required-field check has to be done here or it is lost entirely.
    const emptyFile = fileInputs.find((el) => el.required && el.files.length === 0);
    if (emptyFile) {
      stop();
      this.#promptForApodField(emptyFile, UPLOAD_REQUIRED_MESSAGE());
      return;
    }

    const emptyOther = /** @type {HTMLInputElement[]} */ (
      owned('.apod-field:not([type="file"])')
    ).find((el) => el.required && !String(el.value).trim());
    if (emptyOther) {
      stop();
      this.#promptForApodField(emptyOther, PERSONALIZATION_REQUIRED_MESSAGE());
      return;
    }

    stop();
    form.setAttribute('method', 'post');
    form.setAttribute('enctype', 'multipart/form-data');
    form.submit();
  };

  /**
   * Inline error for a missing APOD field, mirroring promptForSize so the two paths look
   * the same to the shopper.
   * @param {HTMLElement} field
   * @param {string} message
   */
  #promptForApodField(field, message) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');
      const messageSpan = addToCartTextError.querySelector('.product-form-text__error-message');
      if (messageSpan) messageSpan.textContent = message;
      this.#setLiveRegionText(message);
      this.#timeout = setTimeout(() => {
        if (!addToCartTextError) return;
        addToCartTextError.classList.add('hidden');
        this.#clearLiveRegionText();
      }, 5000);
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    field.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    field.focus({ preventScroll: true });
  }

  /**
   * Shows the inline "choose a size" error, announces it, scrolls the size row into
   * view and focuses it. Shared by the click guard and handleSubmit so the two paths
   * can never drift apart.
   * @param {Element} sizeGroup
   * @param {HTMLSelectElement | null} sizeSelect
   * @param {string} message
   */
  promptForSize(sizeGroup, sizeSelect, message) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');
      const messageSpan = addToCartTextError.querySelector('.product-form-text__error-message');
      if (messageSpan) messageSpan.textContent = message;
      this.#setLiveRegionText(message);
      this.#timeout = setTimeout(() => {
        if (!addToCartTextError) return;
        addToCartTextError.classList.add('hidden');
        this.#clearLiveRegionText();
      }, 5000);
    }

    // Bring the size row into view BEFORE focusing. On mobile it is routinely
    // off-screen when the button is tapped, and a bare focus() jumps there abruptly.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    sizeGroup.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });

    const firstSizeInput = sizeGroup.querySelector('input[type="radio"]');
    if (firstSizeInput instanceof HTMLElement) {
      firstSizeInput.focus({ preventScroll: true });
    } else if (sizeSelect instanceof HTMLElement) {
      sizeSelect.focus({ preventScroll: true });
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Enforce size selection if product has a Size option and none selected
    const productRoot = this.closest('.shopify-section, dialog, product-card') || this;
    const sizeGroup = productRoot.querySelector('.variant-option[data-is-size-option="true"]');
    const checkedSizeInput = sizeGroup?.querySelector('input[type="radio"]:checked') ?? null;
    const sizeSelect = /** @type {HTMLSelectElement | null} */ (
      sizeGroup?.querySelector('select.variant-option__select') || null
    );
    const selectHasValue = sizeSelect ? sizeSelect.value !== '' : false;

    const sizePrompt = CHOOSE_SIZE_MESSAGE();

    if (sizeGroup && !checkedSizeInput && !selectHasValue) {
      this.promptForSize(sizeGroup, sizeSelect, sizePrompt);
      return; // Block submission
    }


    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // First, morph the add to cart button to the latest HTML
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Determine if the product has a size option and whether a size is selected
    const variantPicker = this.closest('.shopify-section, dialog, product-card')?.querySelector('variant-picker');
    const sizeFieldset = variantPicker?.querySelector('[data-is-size-option="true"]');
    let sizeSelected = false;
    if (sizeFieldset) {
      const checkedRadio = sizeFieldset.querySelector('input[type="radio"]:checked');
      const selectEl = sizeFieldset.querySelector('select.variant-option__select');
      const selectHasValue = selectEl ? selectEl.value !== '' : false;
      sizeSelected = Boolean(checkedRadio || selectHasValue);
    }

    // Update the button state.
    //
    // "Sold out" and "awaiting a size" are NOT the same state and must not share a
    // branch. Only a genuinely unavailable variant disables the button. A disabled
    // button is not focusable, so disabling it merely because no size is picked put
    // the primary CTA out of reach of keyboard and screen-reader users -- and it is
    // what forced the pointer-events hack that silently swallowed every tap (see the
    // note in snippets/add-to-cart-button.liquid).
    //
    // Awaiting a size now keeps the button LIVE, so a tap reaches handleSubmit, which
    // already renders the inline error, announces it to the live region and focuses
    // the size row. Nothing can be added without a size regardless: variantId is
    // cleared to '' a few lines below, so even with JS broken the form carries no id.
    const unavailable = event.detail.resource == null || event.detail.resource.available == false;
    const needsSize = Boolean(sizeFieldset && !sizeSelected);
    const addToCartBtns = addToCartButtonsIn(addToCartButtonContainer);

    if (unavailable || needsSize) {
      if (unavailable) {
        if (addToCartButtonContainer && typeof addToCartButtonContainer.disable === 'function') {
          addToCartButtonContainer.disable();
        }
      } else if (addToCartButtonContainer && typeof addToCartButtonContainer.enable === 'function') {
        addToCartButtonContainer.enable();
      }
      // Express checkout cannot know the size either, so it stays hidden either way.
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      for (const addToCartBtn of addToCartBtns) {
        addToCartBtn.removeAttribute('data-pj-loading');
        if (needsSize && !unavailable) addToCartBtn.setAttribute('data-pj-needs-size', '');
        else addToCartBtn.removeAttribute('data-pj-needs-size');
      }
      // Button text is deliberately left alone here -- see the note in
      // #updateAddToCartStateForSize. It stays "Add to cart" while awaiting a size.
    } else {
      for (const addToCartBtn of addToCartBtns) {
        addToCartBtn.removeAttribute('data-pj-needs-size');
        addToCartBtn.removeAttribute('data-pj-loading');
      }
      if (addToCartButtonContainer && typeof addToCartButtonContainer.enable === 'function') {
        addToCartButtonContainer.enable();
      }
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      // Restore button text to default when enabled
      const defaultText = Theme?.translations?.add_to_cart || 'Add to cart';
      const addToCartText = addToCartButtonContainer?.querySelector('.add-to-cart-text__content');
      const sizePrompt = Theme?.translations?.select_size_prompt || Theme?.translations?.select_size || 'Select your size';
      if (addToCartText && addToCartText.textContent?.trim() === sizePrompt) {
        addToCartText.textContent = defaultText;
      }
      const addToCartTextError = this.refs.addToCartTextError;
      if (addToCartTextError) addToCartTextError.classList.add('hidden');
    }

    // Update the variant ID only when size is confirmed selected (or no size required)
    console.log('Updating variant ID to:', event.detail ?? '');
    if (!sizeFieldset || sizeSelected) {
      variantId.value = event.detail.resource?.id ?? '';
    } else {
      variantId.value = '';
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
    this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    // Keep button disabled until #onVariantUpdate fires with the confirmed variant ID.
  };

  /**
   * Ensure initial Add to Cart state when size option exists.
   */
  #updateAddToCartStateForSize() {
    const addToCartButtonContainer = this.refs.addToCartButtonContainer;
    const { variantId } = this.refs;
    const variantPicker = this.closest('.shopify-section, dialog, product-card')?.querySelector('variant-picker');
    const sizeFieldset = variantPicker?.querySelector('[data-is-size-option="true"]');
    if (!addToCartButtonContainer || !sizeFieldset) return;

    const checkedRadio = sizeFieldset.querySelector('input[type="radio"]:checked');
    const selectEl = sizeFieldset.querySelector('select.variant-option__select');
    const selectHasValue = selectEl ? selectEl.value !== '' : false;
    const sizeSelected = Boolean(checkedRadio || selectHasValue);

    // Check if variant picker is still loading - don't enable button until fetch completes
    const isLoading = variantPicker?.dataset?.variantLoading === 'true';

    const addToCartText = addToCartButtonContainer?.querySelector('.add-to-cart-text__content');
    const placeholder = Theme?.translations?.select_size_prompt || Theme?.translations?.select_size || 'Select your size';
    const defaultText = Theme?.translations?.add_to_cart || 'Add to cart';

    // Same split as the variantUpdate handler above, and this is the one that runs on
    // FIRST PAINT -- patching only the update path leaves the button disabled for every
    // visitor who lands and taps without touching an option, which is the whole problem.
    // Only `isLoading` (variant data still in flight) may disable the button; merely
    // awaiting a size keeps it live so the tap reaches handleSubmit.
    const addToCartBtns = addToCartButtonsIn(addToCartButtonContainer);
    const needsSize = !sizeSelected;

    if (needsSize || isLoading) {
      if (isLoading) {
        if (addToCartButtonContainer && typeof addToCartButtonContainer.disable === 'function') {
          addToCartButtonContainer.disable();
        }
        // Mark WHY it is disabled. Picking a size disables the button for ~850ms while
        // the variant is fetched, and without this it takes the theme's dead sold-out
        // styling and back again -- which reads as the button flashing. Sold out should
        // look dead; a half-second fetch should not.
        for (const addToCartBtn of addToCartBtns) addToCartBtn.setAttribute('data-pj-loading', '');
      } else {
        for (const addToCartBtn of addToCartBtns) addToCartBtn.removeAttribute('data-pj-loading');
        if (addToCartButtonContainer && typeof addToCartButtonContainer.enable === 'function') {
          addToCartButtonContainer.enable();
        }
      }
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      for (const addToCartBtn of addToCartBtns) {
        if (needsSize && !isLoading) addToCartBtn.setAttribute('data-pj-needs-size', '');
        else addToCartBtn.removeAttribute('data-pj-needs-size');
      }
      if (needsSize) {
        // The button KEEPS its normal "Add to cart" label while awaiting a size
        // (Jo, 2026-09-11) -- the instruction lives in the inline error instead, so the
        // CTA never looks like a different, lesser control. The price suffix is hidden
        // by CSS until a size is chosen, because sizes are not all one price (2XL and
        // 3XL cost more) and showing the S price here would understate it.
        if (variantId) variantId.value = '';
      }
    } else {
      for (const addToCartBtn of addToCartBtns) addToCartBtn.removeAttribute('data-pj-needs-size');
      if (addToCartButtonContainer && typeof addToCartButtonContainer.enable === 'function') {
        addToCartButtonContainer.enable();
      }
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      if (addToCartText && addToCartText.textContent?.trim() === placeholder) {
        addToCartText.textContent = defaultText;
      }
    }
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
